/**
 * 侧边栏标签列表组件
 * 
 * 职责：
 * 1. 展示用户的标签列表
 * 2. 支持标签操作（打开、分享等）
 * 3. 处理标签的激活状态
 */

import {
  MoreHorizontalIcon,
  Plus,
  ChevronRight,
  Pencil,
  TagIcon,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

import {
  useDeleteTagMutation,
  useItemMoveExpandTarget,
  useTagAddDropTarget,
} from "@/modules/mine";
import { CreateTag } from "@/modules/mine/components/create-tag";
import { EditTag } from "@/modules/mine/components/edit-tag";
import { useDialog } from "@/shared/components/dialog";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shared/ui/sidebar";

import type { Tag, RouteInfo } from "./types";


/**
 * 组件 Props 接口
 */
interface SidebarTagsProps {
  items: Tag[];
  routeInfo: RouteInfo;
}

interface SidebarTagItemProps {
  item: Tag;
  routeInfo: RouteInfo;
  isMobile: boolean;
  onEdit: (tag: Tag) => void;
  onDelete: (tag: Tag) => void;
}

function SidebarTagItem({
  item,
  routeInfo,
  isMobile,
  onEdit,
  onDelete,
}: SidebarTagItemProps) {
  const { ref: dropTargetRef, isDropTarget } = useTagAddDropTarget({
    tagId: item.id,
    tagName: item.name,
    disabled: isMobile,
  });

  return (
    <SidebarMenuItem ref={dropTargetRef}>
      <SidebarMenuButton
        asChild
        tooltip={item.name}
        isActive={item.id === routeInfo.tagId}
        className={
          isDropTarget
            ? "bg-primary/10 text-primary ring-2 ring-inset ring-primary/70"
            : undefined
        }
      >
        <Link to={`/mine/tags/${item.id}`}>
          <TagIcon />
          <span>{item.name}</span>
        </Link>
      </SidebarMenuButton>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            showOnHover
            className="rounded-sm data-[state=open]:bg-accent"
          >
            <MoreHorizontalIcon />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-24 rounded-lg"
          side={isMobile ? "bottom" : "right"}
          align={isMobile ? "end" : "start"}
        >
          <DropdownMenuItem onClick={() => onEdit(item)}>
            <Pencil />
            <span>编辑</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onDelete(item)}
            variant="destructive"
          >
            <Trash2 />
            <span>删除</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

/**
 * 侧边栏标签列表组件
 */
function SidebarTags({ items, routeInfo }: SidebarTagsProps) {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const dialog = useDialog();
  const deleteTagMutation = useDeleteTagMutation();
  const [isSectionOpen, setIsSectionOpen] = useState(
    routeInfo.pathname.includes("/tags"),
  );
  const { ref: sectionDropRef, isDropTarget: isSectionDropTarget } =
    useItemMoveExpandTarget("tags", isMobile);

  useEffect(() => {
    if (routeInfo.pathname.includes("/tags")) {
      setIsSectionOpen(true);
    }
  }, [routeInfo.pathname]);

  useEffect(() => {
    if (isSectionOpen || !isSectionDropTarget) {
      return;
    }

    const timer = window.setTimeout(() => setIsSectionOpen(true), 500);
    return () => window.clearTimeout(timer);
  }, [isSectionDropTarget, isSectionOpen]);

  // 打开新建标签对话框
  const handleCreateTag = () => {
    void dialog.open(CreateTag, {}, {
      title: "新建标签",
      description: "创建一个新的标签来标记你的内容",
    });
  };

  const handleEditTag = useCallback((tag: Tag) => {
    void dialog.open(EditTag, { tag }, {
      title: "编辑标签",
      description: "修改标签名称。",
    });
  }, [dialog]);

  const handleDeleteTag = useCallback(async (tag: Tag) => {
    await dialog.confirmDelete({
      entityLabel: "标签",
      entityName: tag.name,
      consequence: "删除标签后，该标签会从已标记的收藏中移除。",
      onConfirm: async () => {
        await deleteTagMutation.mutateAsync(tag.id);
        toast.success("标签删除成功");

        if (routeInfo.tagId === tag.id) {
          navigate(ROUTE_PATHS.MINE_ALL);
        }
      },
    });
  }, [deleteTagMutation, dialog, navigate, routeInfo.tagId]);

  return (
    <SidebarMenu>
      <Collapsible open={isSectionOpen} onOpenChange={setIsSectionOpen}>
        <SidebarMenuItem
          ref={sectionDropRef}
          className="group flex items-center justify-between pr-1"
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="group/header flex min-w-0 flex-1 items-center gap-0.5 rounded-md text-left"
            >
              <SidebarGroupLabel className="text-[13px] font-semibold text-muted-foreground/80">标签</SidebarGroupLabel>
              <ChevronRight className="size-4 text-muted-foreground/60 transition-transform group-data-[state=open]/header:rotate-90" />
              <span className="sr-only">切换标签列表</span>
            </button>
          </CollapsibleTrigger>
          <button
            type="button"
            title="新建标签"
            aria-label="新建标签"
            className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground"
            onClick={handleCreateTag}
          >
            <Plus className="size-4" />
          </button>
        </SidebarMenuItem>
        <CollapsibleContent>
          <SidebarMenu>
            {items.map((item) => (
              <SidebarTagItem
                key={item.id}
                item={item}
                routeInfo={routeInfo}
                isMobile={isMobile}
                onEdit={handleEditTag}
                onDelete={(tag) => void handleDeleteTag(tag)}
              />
            ))}
          </SidebarMenu>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenu>
  );
}

export default SidebarTags;
